import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Send, CheckCircle, Upload, X, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { SEO } from "../components/SEO";

const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;
const CLOUDINARY_CLOUD = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_PRESET = "brsterasz_quotes";

const QuoteRequestPage = () => {
  const [form, setForm] = useState({
    type: "",
    width: "",
    depth: "",
    roofMaterial: "",
    wallType: "",
    wallCount: "",
    shading: [],
    name: "",
    company: "",
    phone: "",
    email: "",
    city: "",
    details: "",
    privacy: false,
  });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    setForm((prev) => ({ ...prev, privacy: e.target.checked }));
  };

  const handleMultiSelect = (field, value) => {
    setForm((prev) => {
      const arr = prev[field];
      if (arr.includes(value)) {
        return { ...prev, [field]: arr.filter((v) => v !== value) };
      }
      return { ...prev, [field]: [...arr, value] };
    });
  };

  const handleFileChange = async (e) => {
    const newFiles = Array.from(e.target.files);
    e.target.value = "";
    if (files.length + newFiles.length > 5) return;

    setUploading(true);
    const uploaded = [];
    for (const file of newFiles) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("upload_preset", CLOUDINARY_PRESET);
        const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/auto/upload`, {
          method: "POST",
          body: fd,
        });
        const data = await res.json();
        if (data.secure_url) {
          uploaded.push({ name: file.name, size: file.size, url: data.secure_url });
        }
      } catch {
        // skip failed uploads silently
      }
    }
    setFiles((prev) => [...prev, ...uploaded].slice(0, 5));
    setUploading(false);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.privacy) {
      setError("Kérjük, fogadja el az adatkezelési tájékoztatót.");
      return;
    }
    setError("");
    setSending(true);

    try {
      const formData = new FormData();
      formData.append("access_key", WEB3FORMS_KEY);
      formData.append("subject", `Ajánlatkérés - ${form.name} - brsterasz.hu`);
      formData.append("from_name", "BRS Terasz Ajánlatkérő");

      // Build structured message
      let message = `AJÁNLATKÉRÉS\n\n`;
      message += `--- Terasz adatok ---\n`;
      message += `Típus: ${form.type || "Nem megadott"}\n`;
      message += `Szélesség: ${form.width ? form.width + " cm" : "Nem megadott"}\n`;
      message += `Mélység: ${form.depth ? form.depth + " cm" : "Nem megadott"}\n`;
      message += `Fedés anyaga: ${form.roofMaterial || "Nem megadott"}\n`;
      message += `Oldalfalak típusa: ${form.wallType || "Nem megadott"}\n`;
      message += `Lefedendő oldalfalak száma: ${form.wallCount || "Nem megadott"}\n`;
      message += `Árnyékolás: ${form.shading.length > 0 ? form.shading.join(", ") : "Nem kért"}\n\n`;
      message += `--- Kapcsolattartói adatok ---\n`;
      message += `Név: ${form.name}\n`;
      message += `Cégnév: ${form.company || "-"}\n`;
      message += `Telefon: ${form.phone}\n`;
      message += `Email: ${form.email}\n`;
      message += `Település: ${form.city}\n\n`;
      message += `--- Projekt részletei ---\n`;
      message += `${form.details || "Nem adott meg további részleteket."}`;

      // Add file URLs to message
      if (files.length > 0) {
        message += `\n\n--- Csatolt fájlok ---\n`;
        files.forEach((f, i) => {
          message += `${i + 1}. ${f.name}: ${f.url}\n`;
        });
      }

      formData.append("message", message);
      formData.append("name", form.name);
      formData.append("email", form.email);
      formData.append("phone", form.phone);

      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      if (data.success) {
        setSent(true);
        setForm({
          type: "", width: "", depth: "", roofMaterial: "", wallType: [], wallCount: "",
          shading: [], name: "", company: "", phone: "", email: "", city: "", details: "", privacy: false,
        });
        setFiles([]);
      } else {
        setError("Hiba történt az üzenet küldésekor. Kérjük próbálja újra.");
      }
    } catch {
      setError("Hiba történt az üzenet küldésekor. Kérjük próbálja újra, vagy hívjon minket telefonon.");
    } finally {
      setSending(false);
    }
  };

  const inputClass = "w-full px-4 py-3 border border-stone-300 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors bg-white";
  const labelClass = "block text-sm font-medium text-slate-700 mb-1.5";

  return (
    <div data-testid="quote-request-page">
      <SEO path="/arajanlat-keres" />

      {/* Hero */}
      <section className="relative py-8 sm:py-10 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="w-12 h-1 bg-amber-500 mb-3" />
          <h1 className="text-2xl sm:text-3xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ajánlatkérés
          </h1>
          <p className="text-stone-300 mt-2 max-w-xl text-sm">
            Az itt megadott adatok alapján tájékoztató jellegű árajánlatot készítünk. A végleges ár minden esetben helyszíni felmérést és egyeztetést követően kerül meghatározásra.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-12">
          {sent ? (
            <div className="bg-green-50 border border-green-200 p-8 text-center" data-testid="quote-success">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Köszönjük ajánlatkérését!</h3>
              <p className="text-slate-600">Hamarosan felvesszük Önnel a kapcsolatot a megadott elérhetőségeken.</p>
              <button onClick={() => setSent(false)} className="mt-6 text-amber-600 font-medium text-sm hover:text-amber-700">
                Új ajánlatkérés
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8" data-testid="quote-form">
              <input type="hidden" name="botcheck" style={{ display: "none" }} />

              {/* Terasz típus */}
              <div>
                <div className="w-12 h-1 bg-amber-500 mb-4" />
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Kérem válasszon
                </h2>
                <div className="space-y-2">
                  {[
                    { value: "falhoz-rogzitett", label: "Terasz/télikert épülethez rögzítve (falhoz rögzített)" },
                    { value: "szabadonallo", label: "Terasz/télikert szabadon álló" },
                  ].map((opt) => (
                    <label key={opt.value} className="flex items-center space-x-3 cursor-pointer group" data-testid={`type-${opt.value}`}>
                      <input
                        type="radio"
                        name="type"
                        value={opt.value}
                        checked={form.type === opt.value}
                        onChange={handleChange}
                        className="w-4 h-4 text-amber-600 border-stone-300 focus:ring-amber-500"
                      />
                      <span className="text-sm text-slate-700 group-hover:text-slate-900">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Méretek */}
              <div>
                <div className="w-12 h-1 bg-amber-500 mb-4" />
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Méretek
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 items-start">
                  <div className="space-y-5">
                    <div>
                      <label className={labelClass}>Szélesség (centiméter)</label>
                      <input
                        type="number"
                        name="width"
                        value={form.width}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="pl. 400"
                        data-testid="quote-width"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Mélység (centiméter)</label>
                      <input
                        type="number"
                        name="depth"
                        value={form.depth}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="pl. 300"
                        data-testid="quote-depth"
                      />
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <img src="/images/dimensions.webp" alt="Szélesség és mélység illusztráció" className="w-full max-w-[240px]" />
                  </div>
                </div>
              </div>

              {/* Fedés anyaga */}
              <div>
                <div className="w-12 h-1 bg-amber-500 mb-4" />
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Fedés anyaga
                </h2>
                <div className="space-y-2">
                  {["Polikarbonát", "Üveg", "Szendvicspanel"].map((opt) => (
                    <label key={opt} className="flex items-center space-x-3 cursor-pointer group" data-testid={`roof-${opt.toLowerCase()}`}>
                      <input
                        type="radio"
                        name="roofMaterial"
                        value={opt}
                        checked={form.roofMaterial === opt}
                        onChange={handleChange}
                        className="w-4 h-4 text-amber-600 border-stone-300 focus:ring-amber-500"
                      />
                      <span className="text-sm text-slate-700 group-hover:text-slate-900">{opt}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Oldalfalak */}
              <div>
                <div className="w-12 h-1 bg-amber-500 mb-4" />
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Oldalfalak típusa
                </h2>
                <p className="text-slate-500 text-xs mb-3">Amennyiben oldalfalat kérne, kérjük adja meg miből készüljön és hány oldalon legyen oldalfal!</p>
                <div className="space-y-2">
                  {[
                    "Eltolható üvegfal",
                    "Polikarbonát fal",
                    "Alumínium lambéria",
                    "Oldalfal nélkül",
                  ].map((opt) => (
                    <label key={opt} className="flex items-center space-x-3 cursor-pointer group" data-testid={`wall-${opt.toLowerCase().replace(/\s+/g, '-')}`}>
                      <input
                        type="radio"
                        name="wallType"
                        checked={form.wallType === opt}
                        onChange={() => setForm((prev) => ({ ...prev, wallType: opt }))}
                        className="w-4 h-4 text-amber-600 border-stone-300 focus:ring-amber-500"
                      />
                      <span className="text-sm text-slate-700 group-hover:text-slate-900">{opt}</span>
                    </label>
                  ))}
                </div>

                <div className="mt-4">
                  <label className={labelClass}>Lefedendő oldalfalak száma</label>
                  <select
                    name="wallCount"
                    value={form.wallCount}
                    onChange={handleChange}
                    className={inputClass}
                    data-testid="quote-wall-count"
                  >
                    <option value="">Válasszon...</option>
                    <option value="1">1 oldal</option>
                    <option value="2">2 oldal</option>
                    <option value="3">3 oldal</option>
                    <option value="4">4 oldal</option>
                  </select>
                </div>
              </div>

              {/* Árnyékolás */}
              <div>
                <div className="w-12 h-1 bg-amber-500 mb-4" />
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Motoros árnyékolás
                </h2>
                <p className="text-slate-500 text-xs mb-3">Több opció is választható</p>
                <div className="space-y-2">
                  {["Motoros tetőárnyékolás", "Motoros oldalárnyékolás"].map((opt) => (
                    <label key={opt} className="flex items-center space-x-3 cursor-pointer group" data-testid={`shading-${opt.toLowerCase()}`}>
                      <input
                        type="checkbox"
                        checked={form.shading.includes(opt)}
                        onChange={() => handleMultiSelect("shading", opt)}
                        className="w-4 h-4 text-amber-600 border-stone-300 focus:ring-amber-500 rounded"
                      />
                      <span className="text-sm text-slate-700 group-hover:text-slate-900">{opt}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Kapcsolattartói adatok */}
              <div>
                <div className="w-12 h-1 bg-amber-500 mb-4" />
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Kapcsolattartói adatok
                </h2>
                <div className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelClass}>Név *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={form.name}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Az Ön neve"
                        data-testid="quote-name"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Cégnév</label>
                      <input
                        type="text"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Cégnév (opcionális)"
                        data-testid="quote-company"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelClass}>Telefon *</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={form.phone}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="+36 ..."
                        data-testid="quote-phone"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Email *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={form.email}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="pelda@email.hu"
                        data-testid="quote-email"
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Település, ahol a terasz megvalósul *</label>
                    <input
                      type="text"
                      name="city"
                      required
                      value={form.city}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="pl. Győr, Budapest, Debrecen"
                      data-testid="quote-city"
                    />
                  </div>
                </div>
              </div>

              {/* Egyéb részletek */}
              <div>
                <div className="w-12 h-1 bg-amber-500 mb-4" />
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Egyéb részletek
                </h2>
                <div>
                  <label className={labelClass}>
                    Írja le, ha egyéb igénye is van, például: korlát, LED világítás, hősugárzó, esővíz elvezető, vagy bármilyen más kiegészítő.
                  </label>
                  <textarea
                    name="details"
                    rows={5}
                    value={form.details}
                    onChange={handleChange}
                    className={`${inputClass} resize-none`}
                    placeholder="Pl.: Szeretnék korlátot is a terasz szélére, LED szalagos világítást a tetőszerkezetbe, és egy hősugárzót. Az anyagok színére vonatkozó igényét is itt jelezheti."
                    data-testid="quote-details"
                  />
                </div>
              </div>

              {/* Fájl feltöltés */}
              <div>
                <div className="w-12 h-1 bg-amber-500 mb-4" />
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Kép vagy dokumentum feltöltése
                </h2>
                <div
                  className={`border-2 border-dashed p-6 text-center transition-colors ${uploading ? 'border-amber-400 bg-amber-50' : 'border-stone-300 hover:border-amber-500 cursor-pointer'}`}
                  onClick={() => !uploading && fileInputRef.current?.click()}
                  data-testid="quote-file-upload"
                >
                  <Upload className={`w-8 h-8 mx-auto mb-2 ${uploading ? 'text-amber-500 animate-pulse' : 'text-stone-400'}`} />
                  <p className="text-sm text-slate-600">{uploading ? 'Feltöltés folyamatban...' : 'Kattintson ide vagy húzza ide a fájlokat'}</p>
                  <p className="text-xs text-slate-400 mt-1">Max. 5 fájl (kép, PDF, DOC)</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                  />
                </div>
                {files.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {files.map((file, i) => (
                      <div key={i} className="flex items-center justify-between bg-stone-50 border border-stone-200 px-3 py-2 text-sm">
                        <div className="flex items-center space-x-2">
                          <FileText className="w-4 h-4 text-amber-500" />
                          <a href={file.url} target="_blank" rel="noopener noreferrer" className="text-slate-700 hover:text-amber-600 truncate max-w-[200px]">{file.name}</a>
                          <span className="text-slate-400 text-xs">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                          data-testid={`remove-file-${i}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Adatvédelmi nyilatkozat */}
              <div className="border-t border-stone-200 pt-6">
                <label className="flex items-start space-x-3 cursor-pointer" data-testid="quote-privacy-checkbox">
                  <input
                    type="checkbox"
                    checked={form.privacy}
                    onChange={handleCheckboxChange}
                    className="w-4 h-4 mt-0.5 text-amber-600 border-stone-300 focus:ring-amber-500 rounded"
                  />
                  <span className="text-sm text-slate-600">
                    Az{" "}
                    <Link to="/adatkezelesi-tajekoztato" target="_blank" className="text-amber-600 hover:text-amber-700 underline">
                      adatkezelési tájékoztatóban
                    </Link>{" "}
                    leírtakat elfogadom. *
                  </span>
                </label>
              </div>

              {error && <p className="text-red-600 text-sm" data-testid="quote-error">{error}</p>}

              <Button
                type="submit"
                disabled={sending || uploading}
                className="rounded-none px-8 py-5 bg-amber-600 text-white hover:bg-amber-700 font-medium text-sm tracking-wider uppercase disabled:opacity-50 w-full sm:w-auto"
                data-testid="quote-submit"
              >
                {sending ? "Küldés..." : (
                  <><Send className="w-4 h-4 mr-2" />Ajánlatkérés elküldése</>
                )}
              </Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default QuoteRequestPage;
